import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import RegisterDialog from "@/components/auth/RegisterDialog";
import { UserType } from "@/types/user";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

interface AccountTypeCardProps {
  account: {
    icon: React.ReactNode;
    title: string;
    description: string;
    benefits: string[];
    color: string;
  };
  index: number;
}

const AccountTypeCard: React.FC<AccountTypeCardProps> = ({
  account,
  index,
}) => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const cardData =
    labels.menuPages?.buyCar?.accountBenefitsSection?.accountTypes?.[index] ||
    {};
  const createLabel =
    labels.buttons?.create ?? (language === "en" ? "Create" : "Skapa");
  const buttonText = cardData.buttonText ?? `${createLabel} ${account.title}`;
  const alreadyLoggedIn =
    labels.menuPages?.buyCar?.accountBenefitsSection?.alreadyLoggedIn ||
    "Du är redan inloggad. Logga ut för att kunna skapa nytt konto.";

  const { isAuthenticated } = useAuth();
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);

  const handleCreateAccount = () => {
    if (isAuthenticated) {
      toast.error(alreadyLoggedIn);
      return;
    }
    const accountType = getAccountType(account.title);
    setShowRegisterDialog(true);
  };

  // Map account title to UserType (supports both sv/en labels)
  const getAccountType = (title: string): UserType => {
    switch (title.toLowerCase()) {
      case "privatkund":
      case "privat":
      case "private":
        return UserType.Individual;
      case "företagskund":
      case "företag":
      case "business":
        return UserType.Business;
      case "handlarkonto":
      case "handlare":
      case "dealer":
        return UserType.Dealer;
      default:
        return UserType.Individual;
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full"
    >
      <div className="flex-1">
        <div
          className={`inline-flex p-3 rounded-full bg-gradient-to-br ${account.color} text-white mb-4`}
        >
          {account.icon}
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">
          {account.title}
        </h3>

        <p className="text-gray-600 mb-4 text-sm">{account.description}</p>

        <ul className="space-y-2">
          {account.benefits.map((benefit, benefitIndex) => (
            <li
              key={benefitIndex}
              className="flex items-center text-gray-700 text-sm"
            >
              <div
                className={`w-2 h-2 rounded-full bg-gradient-to-r ${account.color} mr-3 flex-shrink-0`}
              />
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={handleCreateAccount}
        className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 text-sm bg-gradient-to-r ${account.color} text-white hover:shadow-lg mt-6`}
      >
        {buttonText}
      </button>

      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <RegisterDialog
          open={showRegisterDialog}
          onClose={() => setShowRegisterDialog(false)}
          defaultUserType={getAccountType(account.title)}
        />
      </Dialog>
    </motion.div>
  );
};

export default AccountTypeCard;
